import React, { useEffect, useState } from "react";
import { editSettings, getAllSettings } from "store/actions";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Loader from "components/shared/Loader";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import ImageUpload from "components/shared/ImageUpload";
import { Input, Textarea } from "components/shared/FormComponents";
import SaveChangesButton from "components/shared/SaveChangesButton";
import { toastErrorMessage } from "helpers/toaster/toastErrorMessage";
import { UserPhonesInput } from "components/shared/UserPhonesInput";

const Settings = () => {
  const { formatMessage, locale } = useIntl();
  const dispatch = useDispatch();

  const { loading, error, settings } = useSelector((state) => state.settings);
  const [selectedAppLogo, setSelectedAppLogo] = useState({
    preview: null,
    path: "",
  });
  const [userPhones, setUserPhones] = useState(settings?.userPhones);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    reset({
      appName: settings?.appName,
      appDesc: settings?.appDesc,
      copyright: settings?.copyright,
      locationAddress: settings?.locationAddress,
      phone: settings?.phone,
      whatsup: settings?.whatsup,
      email: settings?.email,
      mapframe: settings?.mapframe,
      userPhones: settings?.userPhones,
      robots: settings?.robots,
      link: settings?.link,
    });

    setSelectedAppLogo({
      preview: null,
      path: settings?.appLogo,
    });
    setUserPhones(settings?.userPhones);
  }, [reset, settings]);

  useEffect(() => {
    dispatch(getAllSettings());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(<FormattedMessage id={"sendingErrorMessage"} />, {
        position: "bottom-left",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }, [error]);

  const addPhone = () => {
    setUserPhones([...userPhones, { name: { en: "", ar: "" }, number: "" }]);
  };

  const deletePhone = (index) => {
    setUserPhones(userPhones?.filter((_, i) => i !== index));
  };

  // error
  useEffect(() => {
    if (error) {
      toastErrorMessage(formatMessage({ id: "sendingErrorMessage" }), locale);
    }
  }, [error, formatMessage, locale]);

  const onSubmit = (data) => {
    if (!selectedAppLogo?.path) {
      toastErrorMessage(formatMessage({ id: "imageErrorMessage" }), locale);
      return;
    }
    data.appLogo = `/uploads/${selectedAppLogo?.path?.split("/").at(-1)}`;
    data.userPhones = userPhones;

    dispatch(editSettings(data));
  };

  if (loading) return <Loader />;

  return (
    <>
      <div className="acc-form">
        <div className="card">
          <div className="card-head">
            <h4>
              <FormattedMessage id={"settings"} />
            </h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Col xl={8} md={12}>
                {/* image  */}
                <Row>
                  <Col lg={12} xs={12}>
                    <div className="form-group required">
                      <h5>
                        <FormattedMessage id={"appLogo"} />
                      </h5>
                      <ImageUpload
                        selectedImage={selectedAppLogo}
                        setSelectedImage={setSelectedAppLogo}
                      />
                    </div>
                  </Col>
                </Row>

                {/* appName  */}
                <Row>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="appName"
                      name="appName.ar"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="appName"
                      name="appName.en"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col lg={6} xs={12}>
                    <Textarea
                      langId="appDesc"
                      name="appDesc.ar"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Textarea
                      langId="appDesc"
                      name="appDesc.en"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="locationAddress"
                      name="locationAddress.title.ar"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="locationAddress"
                      name="locationAddress.title.en"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                  <Row>
                    <Col lg={12} xs={12}>
                      <Input
                        langId="locationLink"
                        name="locationAddress.link"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="copyright"
                      name="copyright.ar"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="copyright"
                      name="copyright.en"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col lg={6} xs={12}>
                    <Input
                      langId="phone"
                      name="phone"
                      dir="ltr"
                      register={register}
                      errors={errors}
                    />
                  </Col>

                  <Col lg={6} xs={12}>
                    <Input
                      langId="whatsup"
                      name="whatsup"
                      dir="ltr"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                </Row>
                <div className="py-3">
                  <h3 className="text-white mb-3">
                    <FormattedMessage id={"contactNums"} />
                  </h3>
                  {userPhones?.map((phone, index) => {
                    return (
                      <Row
                        key={index}
                        className="d-flex flex-row justify-content-center align-items-center"
                      >
                        <Col lg={3} xs={12}>
                          <UserPhonesInput
                            langId="name"
                            name={`userPhones[${index}].name.en`}
                            dir="ltr"
                            register={register}
                            errors={errors}
                            defaultValue={phone?.name?.en}
                            onChange={(e) =>
                              setUserPhones((prev) => {
                                return prev.map((phone, idx) => {
                                  if (idx === index) {
                                    return {
                                      ...phone,
                                      name: {
                                        en: e.target.value,
                                        ar: phone.name.ar,
                                      },
                                    };
                                  }
                                  return prev[idx];
                                });
                              })
                            }
                          />
                        </Col>
                        <Col lg={3} xs={12}>
                          <UserPhonesInput
                            langId="name"
                            name={`userPhones[${index}].name.ar`}
                            dir="ltr"
                            register={register}
                            errors={errors}
                            defaultValue={phone?.name?.ar}
                            onChange={(e) =>
                              setUserPhones((prev) => {
                                return prev.map((phone, idx) => {
                                  if (idx === index) {
                                    return {
                                      ...phone,
                                      name: {
                                        ar: e.target.value,
                                        en: phone.name.en,
                                      },
                                    };
                                  }
                                  return prev[idx];
                                });
                              })
                            }
                          />
                        </Col>

                        <Col lg={4} xs={12}>
                          <UserPhonesInput
                            langId="phone"
                            name={`userPhones[${index}].number`}
                            dir="ltr"
                            register={register}
                            errors={errors}
                            defaultValue={phone?.number}
                            onChange={(e) =>
                              setUserPhones((prev) => {
                                return prev.map((phone, idx) => {
                                  if (idx === index) {
                                    return {
                                      ...phone,
                                      number: e.target.value.toString(),
                                    };
                                  }
                                  return prev[idx];
                                });
                              })
                            }
                          />
                        </Col>

                        <Col lg={2} xs={12}>
                          <Button
                            variant="danger"
                            onClick={() => deletePhone(index)}
                          >
                            <FormattedMessage id="delete" />
                          </Button>
                        </Col>
                      </Row>
                    );
                  })}
                  <Button variant="secondary" onClick={addPhone}>
                    <FormattedMessage id="addContact" />
                  </Button>
                </div>
                <Row>
                  <Col lg={12} xs={12}>
                    <Input
                      langId="email"
                      name="email"
                      dir="ltr"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col lg={12} xs={12}>
                    <Textarea
                      langId="mapframe"
                      dir="ltr"
                      name="mapframe"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col lg={12} xs={12}>
                    <Textarea
                      langId="robots"
                      dir="ltr"
                      name="robots"
                      register={register}
                      errors={errors}
                    />
                  </Col>
                </Row>

                <SaveChangesButton />
              </Col>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;
